import { useEffect, useRef, useState } from "react";
import { cn } from "utils";

export interface ViewMoreProps {
  /** 标题自定义类名 */
  titleClassName?: string;
  /** 容器自定义类名 */
  containerClassName?: string;
  /** 项目自定义类名 */
  itemClassName?: string;
  /** 容器自定义类名 */
  className?: string;
  /** 初始显示的最大行数，默认为4 */
  maxInitialRows?: number;
  /** 查看更多按钮文本 */
  viewMoreText?: React.ReactNode;
  /** 查看更多按钮自定义类名 */
  viewMoreClassName?: string;
  /** 是否启用ViewMore 默认false */
  enableViewMore?: boolean;
}

export interface ViewMoreState {
  containerRef: React.RefObject<HTMLDivElement | null>;
  itemRef: React.RefObject<HTMLDivElement | null>;
  isEmpty: boolean;
  expanded: boolean;
  showViewMore: boolean;
  containerHeight: string | undefined;
  toggleExpand: () => void;
  enableViewMore?: boolean;
}

/**
 * 高阶组件：为组件添加"查看更多"功能
 * @param Component 需要增强的组件
 * @param title 组件标题
 * @param getDataFromProps 从props中获取数据的函数
 * @returns 增强后的组件
 */
export function withViewMore<P extends ViewMoreProps, T>(
  Component: React.ComponentType<
    P & { viewMoreState: ViewMoreState; data: T[] }
  >,
  title: string,
  getDataFromProps: (props: P) => T[] | undefined,
) {
  // 创建一个新组件
  const WithViewMore = (props: P) => {
    const {
      maxInitialRows = 4,
      viewMoreText = "View more",
      enableViewMore,
    } = props;

    // 状态
    const [data, setData] = useState<T[]>([]);
    const [isEmpty, setIsEmpty] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [showViewMore, setShowViewMore] = useState(false);
    const [containerHeight, setContainerHeight] = useState<string | undefined>(
      undefined,
    );

    // 引用
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRef = useRef<HTMLDivElement>(null);

    // 获取数据
    useEffect(() => {
      const rawData = getDataFromProps(props) || [];
      setData(rawData);
      setIsEmpty(rawData.length === 0);
    }, [props]);

    // 计算容器高度
    useEffect(() => {
      // 如果未启用查看更多功能，直接返回
      if (!enableViewMore) {
        setContainerHeight(undefined);
        setShowViewMore(false);
        return;
      }

      if (containerRef.current && itemRef.current && data.length > 0) {
        // 获取一个项的高度
        const itemHeight = itemRef.current.offsetHeight;

        // 获取间隔大小 (gap-4)
        const computedStyle = window.getComputedStyle(containerRef.current);
        const gapSize = parseInt(computedStyle.gap || "16", 10); // 默认为16px

        // 计算每行可以放置的项数量
        const containerWidth = containerRef.current.offsetWidth;
        const itemWidth = itemRef.current.offsetWidth;
        const itemsPerRow = Math.max(
          1,
          Math.floor(containerWidth / (itemWidth + gapSize)),
        );

        // 计算总行数
        const totalRows = Math.ceil(data.length / itemsPerRow);

        // 如果总行数超过最大初始行数，则设置容器高度并显示查看更多按钮
        if (totalRows > maxInitialRows && !expanded) {
          // 计算maxInitialRows行的高度 (包含行间距)
          const limitedHeight =
            itemHeight * maxInitialRows + gapSize * (maxInitialRows - 1);
          setContainerHeight(`${limitedHeight}px`);
          setShowViewMore(true);
        } else {
          setContainerHeight(undefined);
          setShowViewMore(totalRows > maxInitialRows);
        }
      }
    }, [data, expanded, maxInitialRows, enableViewMore]);

    // 切换展开状态
    const toggleExpand = () => {
      if (enableViewMore) {
        setExpanded(true);
        setContainerHeight(undefined);
      }
    };

    // 如果数据为空，返回null
    if (isEmpty) {
      return null;
    }

    // 视图状态对象
    const viewMoreState: ViewMoreState = {
      containerRef,
      itemRef,
      isEmpty,
      expanded,
      showViewMore,
      containerHeight,
      toggleExpand,
      enableViewMore,
    };

    // 渲染增强后的组件
    return (
      <div className={cn("w-full flex flex-col gap-4", props.className)}>
        <div
          className={cn(
            "body_medium_s text-content_secondary",
            props.titleClassName,
          )}
        >
          {title}
        </div>
        <Component {...props} viewMoreState={viewMoreState} data={data} />

        {/* 只在启用查看更多功能、未展开且需要显示查看更多按钮时显示 */}
        {enableViewMore && !expanded && showViewMore && (
          <div className="mt-1 flex justify-start">
            <button
              aria-label="With View More"
              data-openreplay-label={`With View More Button | ${viewMoreText}`}
              id="btn-with-view-more"
              onClick={toggleExpand}
              className={cn(
                "text-content_tertiary body_s cursor-pointer",
                props.viewMoreClassName,
              )}
            >
              {viewMoreText}
            </button>
          </div>
        )}
      </div>
    );
  };

  return WithViewMore;
}
